import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from "react";
import { motion, LayoutGroup } from "framer-motion";
import type { PageProps } from "@/lib/header-config";
import { Footer } from "@/components/Footer";
import { useCursorFollower } from "@/pages/homepage/hooks/useHomepageEffects";
import { MemberCard } from "./components/MemberCard";
import memberData from "./data/member.json";

export function MembersPage({ onHeaderConfig, onHeroReady }: PageProps) {
  useCursorFollower({ forceDarkSurface: true });

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const pointerStartX = useRef(0);
  const pointerDeltaX = useRef(0);
  const isPointerActive = useRef(false);
  const suppressNextClick = useRef(false);
  const interactionTimeoutId = useRef<number | null>(null);
  const scrollIdleTimeoutId = useRef<number | null>(null);
  const mobileFocusTimeoutId = useRef<number | null>(null);
  const canAutoPlay = useRef(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  const baseCount = memberData.length;
  const infiniteMembers = [...memberData, ...memberData, ...memberData];

  const springTransition = { type: "spring" as const, stiffness: 260, damping: 30 };

  const alignCard = useCallback(
    (targetIndex: number, behavior: ScrollBehavior = "smooth") => {
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const targetElement = container.children[targetIndex] as HTMLElement | undefined;
      if (!targetElement) return;

      if (isMobile) {
        const targetCenter = targetElement.offsetLeft + targetElement.offsetWidth / 2;
        const left = targetCenter - container.clientWidth / 2;
        container.scrollTo({ left, behavior });
      } else {
        const paddingOffset = Number.parseFloat(getComputedStyle(container).paddingLeft) || 0;
        const left = targetElement.offsetLeft - paddingOffset;
        container.scrollTo({ left, behavior });
      }
    },
    [isMobile],
  );

  const getStepSize = useCallback(() => {
    if (!scrollRef.current) return isMobile ? 300 : 352;
    const container = scrollRef.current;
    const cardWidth = container.querySelector(".flex-shrink-0")?.clientWidth || (isMobile ? 280 : 320);
    const gap = isMobile ? 20 : 32;
    return cardWidth + gap;
  }, [isMobile]);

  const updateInteraction = () => {
    canAutoPlay.current = false;

    if (interactionTimeoutId.current) {
      window.clearTimeout(interactionTimeoutId.current);
    }

    interactionTimeoutId.current = window.setTimeout(() => {
      canAutoPlay.current = true;
    }, 2000);
  };

  useEffect(() => {
    onHeaderConfig({ activeSection: "history", pageTitle: "임원진", darkHero: true });
    onHeroReady();
    
    if (scrollRef.current) {
      const container = scrollRef.current;
      const oneSetWidth = container.scrollWidth / 3;
      container.scrollLeft = oneSetWidth;
    }

    return () => {
      if (interactionTimeoutId.current) {
        window.clearTimeout(interactionTimeoutId.current);
      }
      if (scrollIdleTimeoutId.current) {
        window.clearTimeout(scrollIdleTimeoutId.current);
      }
      if (mobileFocusTimeoutId.current) {
        window.clearTimeout(mobileFocusTimeoutId.current);
      }
    };
  }, [onHeaderConfig, onHeroReady]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const rotationInterval = isMobile ? 1500 : 2000;

    const autoPlay = setInterval(() => {
      if (
        scrollRef.current &&
        expandedIndex === null &&
        !isDragging &&
        !isPointerDown &&
        !isUserScrolling &&
        canAutoPlay.current
      ) {
        const container = scrollRef.current;
        const step = getStepSize();
        container.scrollBy({
          left: step,
          behavior: "smooth",
        });
      }
    }, rotationInterval);

    return () => clearInterval(autoPlay);
  }, [expandedIndex, getStepSize, isDragging, isMobile, isPointerDown, isUserScrolling]);

  const handleToggle = (index: number) => {
    updateInteraction();
    const normalizedIndex = baseCount + (index % baseCount);
    const isOpening = expandedIndex !== normalizedIndex;
    setExpandedIndex(isOpening ? normalizedIndex : null);

    if (isOpening && !isMobile) {
      alignCard(normalizedIndex, "smooth");
    }
  };

  useLayoutEffect(() => {
    if (!isMobile || expandedIndex === null) return;

    // Keep pre-paint alignment trigger, but animate smoothly for better feel.
    alignCard(expandedIndex, "smooth");
  }, [alignCard, expandedIndex, isMobile]);

  useEffect(() => {
    if (expandedIndex === null || !scrollRef.current) return;

    if (mobileFocusTimeoutId.current) {
      window.clearTimeout(mobileFocusTimeoutId.current);
    }

    // Wait a bit for expand animation so we can measure the full card box.
    mobileFocusTimeoutId.current = window.setTimeout(() => {
      if (!scrollRef.current) return;

      const targetElement = scrollRef.current.children[expandedIndex] as HTMLElement | undefined;
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const bottomSafeArea = isMobile ? 20 : 28;
      const targetBottom = window.innerHeight - bottomSafeArea;
      const overflow = rect.bottom - targetBottom;
      const extraOffset = isMobile ? 30 : 18;

      if (overflow > 0) {
        window.scrollBy({
          top: overflow + extraOffset,
          behavior: "smooth",
        });
      }
    }, 180);

    return () => {
      if (mobileFocusTimeoutId.current) {
        window.clearTimeout(mobileFocusTimeoutId.current);
      }
    };
  }, [expandedIndex, isMobile]);

  const handleScroll = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const oneSetWidth = container.scrollWidth / 3;

    // 즉시 텔레포트 - 동일한 콘텐츠로 이동하므로 사용자가 인식 못함
    if (container.scrollLeft <= 1) {
      container.scrollLeft += oneSetWidth;
      setExpandedIndex(prev => prev === null ? null : baseCount + (prev % baseCount));
    } else if (container.scrollLeft >= oneSetWidth * 2 - 1) {
      container.scrollLeft -= oneSetWidth;
      setExpandedIndex(prev => prev === null ? null : baseCount + (prev % baseCount));
    }

    setIsUserScrolling(true);
    if (scrollIdleTimeoutId.current) window.clearTimeout(scrollIdleTimeoutId.current);
    scrollIdleTimeoutId.current = window.setTimeout(() => setIsUserScrolling(false), 140);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current || !isMobile) return;
    updateInteraction();
    isPointerActive.current = true;
    pointerStartX.current = e.clientX;
    pointerDeltaX.current = 0;
    setIsPointerDown(true);
    setIsDragging(false);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if (!isPointerActive.current) return;
    pointerDeltaX.current = e.clientX - pointerStartX.current;
    if (Math.abs(pointerDeltaX.current) > 6) {
      setIsDragging(true);
    }
  };

  const endPointerGesture = (pointerId: number) => {
    if (!isMobile) return;
    if (!scrollRef.current || !isPointerActive.current) return;
    isPointerActive.current = false;
    setIsPointerDown(false);

    const threshold = isMobile ? 24 : 32;
    const movedX = pointerDeltaX.current;
    suppressNextClick.current = false;

    if (Math.abs(movedX) >= threshold) {
      updateInteraction();
      setExpandedIndex(null);
      const step = getStepSize();
      const direction = movedX < 0 ? 1 : -1;
      scrollRef.current.scrollBy({ left: step * direction, behavior: "smooth" });
      suppressNextClick.current = true;
    }

    setTimeout(() => setIsDragging(false), 50);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    endPointerGesture(e.pointerId);
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    endPointerGesture(e.pointerId);
  };

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressNextClick.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressNextClick.current = false;
  };

  const onWheel = () => {
    if (!isMobile) {
      return;
    }
    updateInteraction();
  };

  return (
    <div className="min-h-screen bg-[#07111f] flex flex-col overflow-x-hidden">
      <main className="flex-1">
        <h1 
          className="text-[40px] md:text-[50px]"
          style={{
            marginTop: "140px",
            color: "#ECECEC",
            textAlign: "center",
            fontFamily: "Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "100%",
          }}
        >
          Team Gameworks 2026
        </h1>

        <section className="relative mt-20 w-full">
          <LayoutGroup>
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onClickCapture={onClickCapture}
              onWheel={onWheel}
              onTouchStart={updateInteraction}
              className={`flex w-full gap-5 md:gap-8 overflow-x-auto md:overflow-x-hidden px-10 md:px-[10vw] pb-80 pt-4 no-scrollbar select-none touch-pan-x ${
                isMobile ? (isDragging ? "cursor-grabbing" : "cursor-grab") : "cursor-default"
              }`}
              style={{ 
                scrollBehavior: "auto",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                touchAction: isMobile ? "pan-y" : "auto",
                // 데스크톱에서는 사용자 스크롤을 막고 모바일에서만 스냅을 사용함
                scrollSnapType: isMobile
                  ? ((isDragging || isPointerDown) ? "none" : "x mandatory")
                  : "none",
              }}
            >
              {infiniteMembers.map((member, index) => (
                <motion.div 
                  layout="position"
                  key={`${member.id}-${index}`} 
                  className="flex-shrink-0" 
                  style={{ 
                    scrollSnapAlign: "center",
                    scrollSnapStop: "always",
                    width: expandedIndex === index
                      ? (isMobile ? 310 : 350)
                      : (isMobile ? 280 : 320)
                  }}
                  transition={springTransition}
                >
                  <MemberCard
                    member={member}
                    isExpanded={expandedIndex === index}
                    isMobile={isMobile}
                    onToggle={() => handleToggle(index)}
                  />
                </motion.div>
              ))}
            </div>
          </LayoutGroup>
          
        </section>
      </main>

      <style>{`
        @keyframes member-hint-bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(6px);
          }
        }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <Footer />
    </div>
  );
}
